import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import StickyBottomNav from "../../../components/Navbar/StickyBottomNav";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { RefreshCw, User, Mail, Phone, School, Users, FileText, AlertCircle, Check, Globe, Calendar } from 'lucide-react';

const BranchRenewal = () => {
  const [formState, setFormState] = useState({
    branchName: "",
    presidentName: "",
    presidentEmail: "",
    presidentPhone: "",
    cabinet: "",
    incomingCabinet: "",
    membershipCount: "",
    communityEngagement: "",
    localCollaborations: "",
    socialMediaLinks: "",
    socialMediaEngagement: "",
    pastYearActivities: "",
    comingYearPlans: "",
    funding: "",
    supportNeeded: "",
    successStories: "",
    renewalAgreement: false
  });

  const [touched, setTouched] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Keep IDs the same as provided
  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSfbAA_Yl1K0CqTIk_mRjGjTv0wk5i-czdXwpD38C56KEGbtpA/formResponse";

  const GOOGLE_FORM_FIELDS = {
    branchName: "entry.1118392054",
    presidentInfo: "entry.1775847480",
    cabinet: "entry.947051141",
    incomingCabinet: "entry.407149924",
    membershipCount: "entry.1305740987",
    communityEngagement: "entry.57939552",
    localCollaborations: "entry.802238698",
    socialMediaInfo: "entry.814706183",
    pastYearActivities: "entry.2016185407",
    comingYearPlans: "entry.1090274887",
    funding: "entry.88618952",
    supportNeeded: "entry.202982690",
    successStories: "entry.1616212746",
    renewalAgreement: "entry.1065543149" 
  };

  const requiredFieldsLabels = {
    branchName: "Branch Name",
    presidentName: "President Name",
    presidentEmail: "President Email",
    presidentPhone: "President Phone Number",
    cabinet: "Cabinet Members",
    incomingCabinet: "Incoming Cabinet Plans",
    membershipCount: "Number of Active Members",
    communityEngagement: "Community Engagement Plans",
    pastYearActivities: "Past Year Activities",
    comingYearPlans: "Plans for Coming Year",
    funding: "Funding Plans",
    supportNeeded: "Support Needed from National Organization",
    renewalAgreement: "Branch Renewal Agreement"
  };

  const validateField = (name, value) => {
    switch (name) {
      case "presidentEmail":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? ""
          : "Please enter a valid email address";
      case "presidentPhone":
        if (!value) return "Phone number is required";
        if (!isValidPhoneNumber(value)) return "Please enter a valid phone number for the selected country";
        return "";
      case "membershipCount":
        if (!value) return "Please enter the number of active members";
        if (isNaN(value) || parseInt(value, 10) < 0) return "Please enter a valid number";
        return "";
      case "renewalAgreement":
        return value === true ? "" : "You must agree to the renewal agreement";
      case "socialMediaLinks":
      case "socialMediaEngagement":
      case "localCollaborations":
      case "successStories":
        return ""; // optional
      default:
        return value && value.toString().trim() ? "" : "This field is required";
    }
  };

  const isInvalid = useCallback((name) => !!(touched[name] && validationErrors[name]), [touched, validationErrors]);

  const handleBlur = (e) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;
    setTouched((p) => ({ ...p, [name]: true }));
    setValidationErrors((p) => ({ ...p, [name]: validateField(name, fieldValue) }));
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setFormState((p) => ({ ...p, [name]: newValue }));
    if (touched[name]) {
      setValidationErrors((p) => ({ ...p, [name]: validateField(name, newValue) }));
    }
  };

  // Separate handler for PhoneInput (no native event)
  const handlePhoneChange = (value) => {
    setFormState((p) => ({ ...p, presidentPhone: value }));
    if (touched.presidentPhone) {
      setValidationErrors((p) => ({ ...p, presidentPhone: validateField("presidentPhone", value) }));
    }
  };

  const handlePhoneBlur = () => {
    setTouched((p) => ({ ...p, presidentPhone: true }));
    setValidationErrors((p) => ({ ...p, presidentPhone: validateField("presidentPhone", formState.presidentPhone) }));
  };

  const scrollToFirstError = () => {
    const el = document.querySelector('[data-error="true"]');
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === 'function') el.focus();
    }
  };

  // Reset only form fields and validation, not success/error messages
  const resetFormFields = () => {
    setFormState({
      branchName: "",
      presidentName: "",
      presidentEmail: "",
      presidentPhone: "",
      cabinet: "",
      incomingCabinet: "",
      membershipCount: "",
      communityEngagement: "",
      localCollaborations: "",
      socialMediaLinks: "",
      socialMediaEngagement: "",
      pastYearActivities: "",
      comingYearPlans: "",
      funding: "",
      supportNeeded: "",
      successStories: "",
      renewalAgreement: false
    });
    setTouched({});
    setValidationErrors({});
  };

  // Complete reset including messages (for manual reset)
  const resetForm = () => {
    resetFormFields();
    setErrorMessage("");
    setShowErrorMessage(false);
    setShowSuccessMessage(false);
  };

  const postGoogle = async (formData) => {
    try {
      await fetch(GOOGLE_FORM_URL, { method: "POST", body: formData, mode: "no-cors" });
      return true; // opaque response, assume success if no throw
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setErrorMessage("");

    // validate all
    const errs = {};
    Object.entries(formState).forEach(([name, val]) => {
      const err = validateField(name, val);
      if (err) errs[name] = err;
    });

    setValidationErrors(errs);
    setTouched(Object.keys(formState).reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    if (Object.keys(errs).length) {
      // build message of missing required fields
      const missing = Object.keys(errs)
        .filter((f) => requiredFieldsLabels[f])
        .map((f) => requiredFieldsLabels[f]);
      if (missing.length) {
        setErrorMessage(`Please complete the following required fields: ${missing.join(", ")}`);
      } else {
        setErrorMessage("Please correct the errors in the form before submitting.");
      }
      setShowErrorMessage(true);
      scrollToFirstError();
      setIsSubmitting(false);
      return;
    }

    try {
      if (!GOOGLE_FORM_URL) throw new Error("Google Form URL not configured");

      const fd = new FormData();

      // Combine president info
      const presidentInfo = `Name: ${formState.presidentName}\nEmail: ${formState.presidentEmail}\nPhone: ${formState.presidentPhone}`;
      fd.append(GOOGLE_FORM_FIELDS.presidentInfo.trim(), presidentInfo);

      // Combine social media info
      const socialMediaInfo = `Social Media Links: ${formState.socialMediaLinks || 'None provided'}\n\nEngagement Description: ${formState.socialMediaEngagement || 'None provided'}`;
      fd.append(GOOGLE_FORM_FIELDS.socialMediaInfo.trim(), socialMediaInfo);

      const skip = new Set(["presidentName", "presidentEmail", "presidentPhone", "socialMediaLinks", "socialMediaEngagement"]);

      Object.entries(formState).forEach(([key, value]) => {
        if (!GOOGLE_FORM_FIELDS[key] || skip.has(key)) return;
        const fieldId = GOOGLE_FORM_FIELDS[key].trim();
        if (key === "renewalAgreement") {
          if (value === true) fd.append(fieldId, "Agree");
          return;
        }
        fd.append(fieldId, String(value ?? ""));
      });

      const ok = await postGoogle(fd);
      if (!ok) throw new Error("Network/CORS failure");

      // Store the email before resetting the form
      const submittedEmail = formState.presidentEmail;
      
      setShowSuccessMessage(true);
      resetFormFields(); // Only reset form fields, not success message
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => document.querySelector('[role="alert"]')?.focus(), 400);
      
    } catch (err) {
      console.error("Submission error:", err);
      setErrorMessage("We're experiencing technical difficulties. Please try again later or email hello@studentsforrefugees.org.");
      setShowErrorMessage(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (showSuccessMessage) {
      const t = setTimeout(() => setShowSuccessMessage(false), 15000);
      return () => clearTimeout(t);
    }
  }, [showSuccessMessage]);

  useEffect(() => {
    if (showErrorMessage) {
      const t = setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage("");
      }, 10000);
      return () => clearTimeout(t);
    }
  }, [showErrorMessage]);

  const inputClassName = (name) => `w-full px-4 py-3 rounded-lg transition-all duration-200 border ${isInvalid(name) ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-lightTeal focus:ring-2 focus:ring-primary focus:border-primary'} ${isSubmitting ? 'bg-offWhite' : 'bg-white'}`;

  const phoneInputClassName = `w-full px-4 py-3 rounded-lg transition-all duration-200 border ${isInvalid('presidentPhone') ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-lightTeal focus:ring-2 focus:ring-primary focus:border-primary'} ${isSubmitting ? 'bg-offWhite' : 'bg-white'}`;

  // Custom styles for phone input
  const phoneInputCustomStyles = `
    .PhoneInputCountry { margin-right: 0.75rem; }
    .PhoneInputCountrySelect { padding: 0.5rem; border-radius: 0.5rem; border-color: #E8F3F4; }
    .PhoneInputInput { font-family: 'Lato', sans-serif; padding: 0.75rem 1rem; border-radius: 0.5rem; border-color: #E8F3F4; }
    .PhoneInputInput:focus { border-color: #53C2C8; outline: none; box-shadow: 0 0 0 2px rgba(83, 194, 200, 0.2); }
  `;

  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />

      <div className="py-16 px-6 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-display text-h1 text-secondary mb-4">Branch Renewal</h1>
            <p className="text-bodyRegular text-textPrimary max-w-3xl mx-auto mb-6">
              Thank you for your continued commitment to Students For Refugees.
              Please complete this renewal form to maintain your chapter status
              for the upcoming year.
            </p>
            <div className="w-16 h-1 bg-cta mx-auto mb-6" />
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-lg overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Notifications */}
            {showSuccessMessage && (
              <motion.div
                className="bg-green-50 border-l-4 border-green-500 p-6 mb-6"
                role="alert"
                tabIndex={-1}
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                <div className="flex items-start">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, duration: 0.3, ease: "easeOut" }}>
                    <Check size={28} className="text-green-500 mr-4 flex-shrink-0 mt-1" />
                  </motion.div>
                  <div className="flex-1">
                    <motion.h4 className="font-display text-xl text-green-700 mb-3" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3, duration: 0.4 }}>
                      🎉 Branch Renewal Successfully Submitted!
                    </motion.h4>
                    <motion.div className="space-y-2 text-green-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.4 }}>
                      <p className="font-medium">
                        Thank you for renewing your Students for Refugees chapter! Your renewal application has been received and is being processed.
                      </p>
                      <div className="bg-green-100 p-4 rounded-lg mt-4">
                        <h5 className="font-bold text-green-800 mb-2">What happens next?</h5>
                        <ul className="space-y-1 text-sm text-green-700">
                          <li>• Our team will review your renewal within <strong>5-10 business days</strong></li>
                          <li>• If we need any additional information, we'll contact you directly</li>
                        </ul>
                      </div>
                      <p className="text-sm text-green-600 mt-3">
                        <strong>Questions?</strong> Contact us at{" "}
                        <a href="mailto:hello@studentsforrefugees.org" className="underline font-medium hover:text-green-800">
                          hello@studentsforrefugees.org
                        </a>
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            )}

            {showErrorMessage && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6" role="alert" tabIndex={-1}>
                <div className="flex items-start">
                  <AlertCircle size={24} className="text-red-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-display text-lg text-red-700 mb-1">Submission Error</h4>
                    <p className="text-red-600">{errorMessage || "There was an error submitting your renewal. Please try again later or contact us directly if the problem persists."}</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="p-6 md:p-8" noValidate>
              {/* Branch Information */}
              <section className="mb-10">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-3">
                    <School size={20} className="text-white" />
                  </div>
                  <h2 className="font-display text-h3 text-secondary">Branch Information</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Branch Name (School) <span className="text-cta">*</span>
                    </label>
                    <input
                      type="text"
                      name="branchName"
                      value={formState.branchName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={inputClassName("branchName")}
                      required
                      disabled={isSubmitting}
                      aria-invalid={isInvalid("branchName")}
                      data-error={isInvalid("branchName")}
                      placeholder="Enter your school/institution name"
                    />
                    {isInvalid("branchName") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.branchName}</p>
                    )}
                  </div>
                </div>
              </section>

              {/* Branch President Information */}
              <section className="mb-10">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-3">
                    <User size={20} className="text-white" />
                  </div>
                  <h2 className="font-display text-h3 text-secondary">Branch President</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      President Name <span className="text-cta">*</span>
                    </label>
                    <input
                      type="text"
                      name="presidentName"
                      value={formState.presidentName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={inputClassName("presidentName")}
                      required
                      disabled={isSubmitting}
                      aria-invalid={isInvalid("presidentName")}
                      data-error={isInvalid("presidentName")}
                      placeholder="Full name of current president"
                    />
                    {isInvalid("presidentName") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.presidentName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      President Email <span className="text-cta">*</span>
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        name="presidentEmail"
                        value={formState.presidentEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${inputClassName("presidentEmail")} pl-10`}
                        required
                        disabled={isSubmitting}
                        aria-invalid={isInvalid("presidentEmail")}
                        data-error={isInvalid("presidentEmail")}
                        placeholder="president@email.com"
                      />
                    </div>
                    {isInvalid("presidentEmail") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.presidentEmail}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      President Phone Number <span className="text-cta">*</span>
                    </label>
                    <div
                      className="relative"
                      aria-invalid={isInvalid("presidentPhone")}
                      data-error={isInvalid("presidentPhone")}
                    >
                      <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <PhoneInput
                        international
                        defaultCountry="US"
                        value={formState.presidentPhone}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneBlur}
                        className={`${phoneInputClassName} pl-10`}
                        disabled={isSubmitting}
                      />
                    </div>
                    {isInvalid("presidentPhone") && (
                      <p className="mt-1 text-sm text-red-500" role="alert">{validationErrors.presidentPhone}</p>
                    )}
                  </div>
                </div>
              </section>

              {/* Cabinet Information */}
              <section className="mb-10">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-3">
                    <Users size={20} className="text-white" />
                  </div>
                  <h2 className="font-display text-h3 text-secondary">Cabinet & Membership</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Cabinet <span className="text-cta">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-2">Please include the name of all cabinet members along with their positions</p>
                    <textarea
                      name="cabinet"
                      value={formState.cabinet}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={inputClassName("cabinet")}
                      required
                      disabled={isSubmitting}
                      aria-invalid={isInvalid("cabinet")}
                      data-error={isInvalid("cabinet")}
                      placeholder="Example: Vice President - Jane Doe, Secretary - John Smith, Treasurer - Mary Johnson"
                    />
                    {isInvalid("cabinet") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.cabinet}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Incoming Cabinet <span className="text-cta">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-2">What are your plans to onboard a new cabinet?</p>
                    <textarea
                      name="incomingCabinet"
                      value={formState.incomingCabinet}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={inputClassName("incomingCabinet")}
                      required
                      disabled={isSubmitting}
                      aria-invalid={isInvalid("incomingCabinet")}
                      data-error={isInvalid("incomingCabinet")}
                      placeholder="Describe your transition plans and onboarding process..."
                    />
                    {isInvalid("incomingCabinet") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.incomingCabinet}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Number of Active Members <span className="text-cta">*</span>
                    </label>
                    <input
                      type="number"
                      name="membershipCount"
                      value={formState.membershipCount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={inputClassName("membershipCount")}
                      required
                      disabled={isSubmitting}
                      min="0"
                      aria-invalid={isInvalid("membershipCount")}
                      data-error={isInvalid("membershipCount")}
                      placeholder="0"
                    />
                    {isInvalid("membershipCount") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.membershipCount}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Community Engagement <span className="text-cta">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-2">How do you plan to further engage students, faculty, and the broader community in your work?</p>
                    <textarea
                      name="communityEngagement"
                      value={formState.communityEngagement}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={inputClassName("communityEngagement")}
                      required
                      disabled={isSubmitting}
                      aria-invalid={isInvalid("communityEngagement")}
                      data-error={isInvalid("communityEngagement")}
                      placeholder="Describe your engagement strategies..."
                    />
                    {isInvalid("communityEngagement") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.communityEngagement}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">Local Collaborations</label>
                    <p className="text-sm text-gray-600 mb-2">Are there any local refugee communities or organizations that you would aim to collaborate with or have already done so?</p>
                    <textarea
                      name="localCollaborations"
                      value={formState.localCollaborations}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={inputClassName("localCollaborations")}
                      disabled={isSubmitting}
                      placeholder="List current and potential community partners..."
                    />
                  </div>
                </div>
              </section>

              {/* Social Media & Online Presence */}
              <section className="mb-10">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-3">
                    <Globe size={20} className="text-white" />
                  </div>
                  <h2 className="font-display text-h3 text-secondary">Social Media & Online Presence</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">Social Media Links</label>
                    <p className="text-sm text-gray-600 mb-2">Please include links to any social media pages you run</p>
                    <textarea
                      name="socialMediaLinks"
                      value={formState.socialMediaLinks}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={3}
                      className={inputClassName("socialMediaLinks")}
                      disabled={isSubmitting}
                      placeholder="Instagram: @yourhandle, Facebook: facebook.com/yourpage, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">Social Media Engagement</label>
                    <p className="text-sm text-gray-600 mb-2">How has your engagement been? What has been successful?</p>
                    <textarea
                      name="socialMediaEngagement"
                      value={formState.socialMediaEngagement}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={inputClassName("socialMediaEngagement")}
                      disabled={isSubmitting}
                      placeholder="Describe your social media success stories and engagement metrics..."
                    />
                  </div>
                </div>
              </section>

              {/* Activities & Planning */}
              <section className="mb-10">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-3">
                    <Calendar size={20} className="text-white" />
                  </div>
                  <h2 className="font-display text-h3 text-secondary">Activities & Planning</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Branch Activities for the Past Year <span className="text-cta">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-2">Please describe the activities your branch has been involved with in the past year</p>
                    <textarea
                      name="pastYearActivities"
                      value={formState.pastYearActivities}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={5}
                      className={inputClassName("pastYearActivities")}
                      required
                      disabled={isSubmitting}
                      aria-invalid={isInvalid("pastYearActivities")}
                      data-error={isInvalid("pastYearActivities")}
                      placeholder="Detail your events, fundraisers, community outreach, educational programs, etc."
                    />
                    {isInvalid("pastYearActivities") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.pastYearActivities}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Plans for the Coming Year <span className="text-cta">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-2">What are your goals for the upcoming year?</p>
                    <textarea
                      name="comingYearPlans"
                      value={formState.comingYearPlans}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={5}
                      className={inputClassName("comingYearPlans")}
                      required
                      disabled={isSubmitting}
                      aria-invalid={isInvalid("comingYearPlans")}
                      data-error={isInvalid("comingYearPlans")}
                      placeholder="Outline your goals, planned activities, and initiatives..."
                    />
                    {isInvalid("comingYearPlans") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.comingYearPlans}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Funding Plans <span className="text-cta">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-2">How do you plan to fund chapter activities (e.g., through fundraisers, school grants, donations)? Note that each branch must pay $80 annually for chapter dues.</p>
                    <textarea
                      name="funding"
                      value={formState.funding}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={inputClassName("funding")}
                      required
                      disabled={isSubmitting}
                      aria-invalid={isInvalid("funding")}
                      data-error={isInvalid("funding")}
                      placeholder="Detail your funding strategy including the $80 annual chapter dues..."
                    />
                    {isInvalid("funding") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.funding}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Support from National Organization <span className="text-cta">*</span>
                    </label>
                    <p className="text-sm text-gray-600 mb-2">What kind of support would your branch like to receive from the national organization?</p>
                    <textarea
                      name="supportNeeded"
                      value={formState.supportNeeded}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={inputClassName("supportNeeded")}
                      required
                      disabled={isSubmitting}
                      aria-invalid={isInvalid("supportNeeded")}
                      data-error={isInvalid("supportNeeded")}
                      placeholder="Describe what resources, guidance, or support would help your branch succeed..."
                    />
                    {isInvalid("supportNeeded") && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.supportNeeded}</p>
                    )}
                  </div>
                </div>
              </section>

              {/* Success Stories */}
              <section className="mb-10">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-3">
                    <FileText size={20} className="text-white" />
                  </div>
                  <h2 className="font-display text-h3 text-secondary">Success Stories</h2>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Additional Information</label>
                  <p className="text-sm text-gray-600 mb-2">Please feel free to include anything you wish that you deem fit</p>
                  <textarea
                    name="successStories"
                    value={formState.successStories}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={4}
                    className={inputClassName("successStories")}
                    disabled={isSubmitting}
                    placeholder="Share your achievements, impact stories, or anything else you'd like us to know..."
                  />
                </div>
              </section>

              {/* Renewal Agreement */}
              <section className="mb-10">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-3">
                    <RefreshCw size={20} className="text-white" />
                  </div>
                  <h2 className="font-display text-h3 text-secondary">Branch Renewal Agreement</h2>
                </div>

                <div className="p-4 bg-lightTeal rounded-lg mb-6">
                  <p className="text-textPrimary mb-4">
                    By submitting this form, we confirm that our branch is actively involved in promoting the mission and goals of
                    <strong> Students for Refugees</strong> and will continue to adhere to the organization's values and guidelines.
                    We also agree to remain in contact with the national organization and provide any necessary updates or reports as requested.
                  </p>

                  <div className="flex items-start">
                    <div className="flex items-center h-5 mt-1">
                      <input
                        id="renewalAgreement"
                        name="renewalAgreement"
                        type="checkbox"
                        checked={formState.renewalAgreement}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary ${isInvalid('renewalAgreement') ? 'border-red-500' : ''}`}
                        required
                        disabled={isSubmitting}
                        aria-invalid={isInvalid('renewalAgreement')}
                        data-error={isInvalid('renewalAgreement')}
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="renewalAgreement" className="text-sm text-textPrimary font-medium cursor-pointer">
                        I agree to the Branch Renewal Agreement <span className="text-cta">*</span>
                      </label>
                      {isInvalid('renewalAgreement') && (
                        <p className="mt-1 text-sm text-red-500">{validationErrors.renewalAgreement}</p>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-cta text-white py-4 px-8 rounded-xl text-button font-body transition-all duration-300 shadow-md ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'}`}
                whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
              >
                {isSubmitting ? "Submitting..." : "Submit Branch Renewal"}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>

      <Footer />
      <StickyBottomNav />

      {/* Custom phone input styles */}
      <style jsx global>{phoneInputCustomStyles}</style>
    </div>
  );
};

export default BranchRenewal;